import useIsMobile from '../../Hooks/useIsMobile';
import { useState, useEffect, useRef, useCallback } from 'react';

const CountdownTimer = ({ 
  expiryDate, 
  onExpire = () => {},
  className = "",
  size = "md", // sm, md, lg
  showFlash = false, // Show flashing effect when true
  text = null, // Custom text function(timeLeft)
  offerType = 'default' // 'default', 'blackout'
}) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const hasExpiredRef = useRef(false); // Track if onExpire has been called

  // Memoize onExpire to prevent dependency changes
  const memoizedOnExpire = useCallback(onExpire, []);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!expiryDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiryDate).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        // Call onExpire only once
        if (!hasExpiredRef.current) {
          hasExpiredRef.current = true;
          memoizedOnExpire();
        }
        return null;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    // Initial calculation
    const initial = calculateTimeLeft();
    setTimeLeft(initial);
    
    // If already expired, don't start the timer
    if (!initial) {
      return;
    }

    // Update every second
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      
      if (!remaining) {
        clearInterval(timer);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [expiryDate, memoizedOnExpire]);

  if (!timeLeft) return null;

  const badgeFlashingStyle = showFlash ? {
    animation: 'badge-flash-pulse 2s ease-in-out infinite'
  } : {};

  const flashingKeyframes = showFlash ? (
    <style>{`
      @keyframes badge-flash-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
      }
    `}</style>
  ) : null;

  const formatTimeUnit = (value, unit) => {
    const pluralUnit = value === 1 ? unit : `${unit}s`;
    return `${value} ${pluralUnit}`;
  };

  const getTimerText = (text) => {
    if (offerType === 'blackout') {
      return 'Flash Offer Ends Soon';
    }

    if (text && typeof text === 'string') {
      return text;
    }

    const { hours, minutes, seconds } = timeLeft;
    let timeString = '';
    
    if (hours > 0) {
      timeString = `${formatTimeUnit(hours, 'hour')} ${formatTimeUnit(minutes, 'min')}`;
    } else if (minutes > 0) {
      timeString = formatTimeUnit(minutes, 'min');
    } else {
      timeString = formatTimeUnit(seconds, 'sec');
    }
    
    return showFlash ? `${isMobile ? '' : 'Limited'} Offer Ends in ${timeString}` : timeString;
  };

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  return (
    <>
      {flashingKeyframes}
      <div 
        className={`font-medium text-red-600 ${sizeClasses[size]} ${className} w-fit ${!isMobile && showFlash ? "bg-red-100 border border-sahi-red rounded-md px-2 py-1" : ""}`}
        style={badgeFlashingStyle}
      >
        <span>
          {getTimerText(text)}
        </span>
      </div>
    </>
  );
};

export default CountdownTimer;